import React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import EditIcon from '@mui/icons-material/Edit';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Box, Chip, IconButton, Stack } from '@mui/material';

import Image from '~/components/Common/Image';

import type { RoomImage } from './utils';

interface Props {
  canToggleHidden: boolean;
  hasFrame?: boolean;
  image: RoomImage;
  isHidden: boolean;
  onDeleteImage: (publicId: string) => void;
  onEditImage: (publicId: string) => void;
  onToggleHiddenImage: (publicId: string) => Promise<void>;
  slotLabel: string;
}

export default function ImagesByRoomSortableSlot({
  canToggleHidden,
  hasFrame = true,
  image,
  isHidden,
  onDeleteImage,
  onEditImage,
  onToggleHiddenImage,
  slotLabel,
}: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: image.publicId });

  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        aspectRatio: '16 / 9',
        width: '100%',
        border: hasFrame ? 1 : 0,
        borderColor: 'divider',
        borderRadius: hasFrame ? 1 : 0,
        bgcolor: 'background.default',
        cursor: isDragging ? 'grabbing' : 'grab',
        opacity: isDragging ? 0.72 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 2 : 'auto',
        '&:hover .OfferBuilderImagesByRoom-overlay': {
          opacity: 1,
          visibility: 'visible',
          pointerEvents: 'auto',
        },
      }}
    >
      <Image
        publicId={image.publicId}
        alt={image.title || 'Room image'}
        options={{ width: 320, height: 180 }}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          filter: isHidden ? 'grayscale(1) opacity(0.42)' : undefined,
        }}
      />
      <Chip label={slotLabel} size="small" sx={roomImageSlotChipSx} />
      {isHidden && <Chip label="HIDDEN" size="small" sx={roomImageHiddenChipSx} />}
      <Stack
        className="OfferBuilderImagesByRoom-overlay"
        direction="row"
        alignItems="flex-end"
        justifyContent="flex-end"
        spacing={0.75}
        sx={roomImageOverlaySx}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <IconButton aria-label="Edit room image" sx={imageActionButtonSx} onClick={() => onEditImage(image.publicId)}>
          <EditIcon fontSize="small" />
        </IconButton>
        {canToggleHidden && (
          <IconButton
            aria-label={isHidden ? 'Show room image' : 'Hide room image'}
            sx={imageActionButtonSx}
            onClick={() => void onToggleHiddenImage(image.publicId)}
          >
            <VisibilityOffIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton
          aria-label="Remove image from room"
          sx={imageActionButtonSx}
          onClick={() => onDeleteImage(image.publicId)}
        >
          <RemoveCircleOutlineIcon color="error" fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}

const imageActionButtonSx = {
  width: 34,
  height: 34,
  bgcolor: 'common.white',
  color: 'grey.800',
  '&:hover': {
    bgcolor: 'grey.100',
  },
};

const roomImageSlotChipSx = {
  position: 'absolute',
  top: 10,
  left: 10,
  bgcolor: 'grey.800',
  color: 'common.white',
  fontWeight: 700,
};

const roomImageHiddenChipSx = {
  position: 'absolute',
  top: 10,
  right: 10,
  bgcolor: 'grey.800',
  color: 'common.white',
  fontWeight: 700,
};

const roomImageOverlaySx = {
  position: 'absolute',
  right: 0,
  bottom: 0,
  left: 0,
  opacity: 0,
  visibility: 'hidden',
  pointerEvents: 'none',
  transition: 'opacity 160ms ease-in-out, visibility 160ms ease-in-out',
  px: 1.25,
  py: 1,
  background: 'linear-gradient(180deg, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.62))',
};
